import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import Logo from '../../components/Logo';
import { 
  Shield, 
  CheckCircle2, 
  Car, 
  Calendar, 
  Euro, 
  Building2,
  AlertTriangle,
  FileText,
  Wrench,
  Award,
  Lock,
  BadgeCheck,
  Upload,
  CreditCard,
  Clock,
  X,
  Image,
  Download,
  Camera,
  FolderOpen,
  Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';

// Image compression utility for mobile uploads
const compressImage = (file, maxSizeMB = 1) => {
  return new Promise((resolve, reject) => {
    // If file is not an image or is a PDF, return as-is
    if (!file.type.startsWith('image/') || file.type === 'application/pdf') {
      resolve(file);
      return;
    }

    // If file is already small enough, return as-is
    if (file.size <= maxSizeMB * 1024 * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate new dimensions (max 1920px on longest side)
        const maxDimension = 1920;
        if (width > height && width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Compress with quality 0.7
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              console.log(`Compressed ${file.name}: ${(file.size/1024).toFixed(0)}KB -> ${(blob.size/1024).toFixed(0)}KB`);
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          0.7
        );
      };
      img.onerror = () => resolve(file);
      img.src = event.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

// Informations légales C2 Assurances
const C2_INFO = {
  name: "C2 ASSURANCES",
  orias: "24000613",
  address: "16 Place des Quinconces, 33000 Bordeaux",
  acpr_address: "4 Place de Budapest, 75009 Paris",
  role: "Cabinet de conseil et courtier en assurances"
};

const ThirdPartyPortal = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('welcome');
  const [submitting, setSubmitting] = useState(false);
  const [refuseReason, setRefuseReason] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedSettlement, setAcceptedSettlement] = useState(false);
  const quoteInputRef = useRef(null);
  const quoteCameraRef = useRef(null);
  const ribInputRef = useRef(null);
  const ribCameraRef = useRef(null);
  
  // Detect if on mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Garage info
  const [garage, setGarage] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: ''
  });
  
  // Quote and RIB files
  const [quoteFile, setQuoteFile] = useState(null);
  const [ribFile, setRibFile] = useState(null);
  const [quoteAmount, setQuoteAmount] = useState('');

  // Third party contact info
  const [thirdPartyInfo, setThirdPartyInfo] = useState({
    full_name: '',
    address: '',
    email: '',
    phone: ''
  });

  const refreshData = async () => {
    try {
      const response = await axios.get(`${API_URL}/portal/third-party/${token}`);
      setData(response.data);
      return response.data;
    } catch (err) {
      console.error('Error refreshing data:', err);
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/portal/third-party/${token}`);
        setData(response.data);
        
        // Set step based on status
        const status = response.data.status;
        if (status === 'tiers_refused') {
          setStep('refused');
        } else if (status === 'tiers_accepted') {
          setStep('upload_quote');
        } else if (status === 'quote_received') {
          setStep('waiting_validation');
        } else if (status === 'quote_validated' || status === 'signature_pending') {
          setStep('settlement');
        } else if (status === 'signed') {
          setStep('waiting_payment');
        } else if (status === 'payment_confirmed' || status === 'payment_pending') {
          setStep('waiting_repair');
        } else if (status === 'completed') {
          setStep('completed');
        } else {
          setStep('welcome');
        }
        
        // Pre-fill third party info if available
        if (response.data.third_party) {
          setThirdPartyInfo({
            full_name: `${response.data.third_party.first_name || ''} ${response.data.third_party.last_name || ''}`.trim(),
            address: response.data.third_party.address || '',
            email: response.data.third_party.email || '',
            phone: response.data.third_party.phone || ''
          });
        }
      } catch (err) {
        setError('Ce lien est invalide ou a expiré.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleAccept = async () => {
    if (!acceptedTerms) {
      toast.error('Veuillez accepter les conditions pour continuer');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/portal/third-party/${token}/accept`);
      await refreshData();
      setStep('upload_quote');
      toast.success('Procédure amiable acceptée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'acceptation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefuse = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/portal/third-party/${token}/refuse`, null, {
        params: { reason: refuseReason }
      });
      setStep('refused');
      toast.success('Choix enregistré');
    } catch (err) {
      toast.error('Erreur lors du refus');
    } finally {
      setSubmitting(false);
    }
  };

  // Backup refs for Android resilience
  const garageBackupRef = useRef(garage);
  const quoteAmountBackupRef = useRef(quoteAmount);
  
  // Keep backup refs in sync
  useEffect(() => { garageBackupRef.current = garage; }, [garage]);
  useEffect(() => { quoteAmountBackupRef.current = quoteAmount; }, [quoteAmount]);

  const handleQuoteUpload = async () => {
    if (!garage.name || !quoteFile || !ribFile || !quoteAmount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    // Save form state before upload attempt (Android resilience)
    const savedGarage = { ...garageBackupRef.current };
    const savedQuoteAmount = quoteAmountBackupRef.current;
    const savedQuoteFile = quoteFile;
    const savedRibFile = ribFile;
    
    setSubmitting(true);
    try {
      toast.info('Envoi en cours... Ne fermez pas l\'application.');
      
      const formData = new FormData();
      formData.append('quote_file', savedQuoteFile);
      formData.append('rib_file', savedRibFile);
      formData.append('quote_amount', savedQuoteAmount);
      formData.append('garage_name', savedGarage.name);
      formData.append('garage_address', `${savedGarage.address || ''}, ${savedGarage.postal_code || ''} ${savedGarage.city || ''}`.trim());
      formData.append('garage_phone', savedGarage.phone || '');
      formData.append('garage_email', savedGarage.email || '');
      
      await axios.post(`${API_URL}/portal/third-party/${token}/upload-quote`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000
      });
      
      await refreshData();
      setStep('waiting_validation');
      toast.success('Documents envoyés avec succès');
    } catch (err) {
      console.error('Upload error:', err);
      
      // Restore ALL form state on error (critical for Android)
      setGarage(savedGarage);
      setQuoteAmount(savedQuoteAmount);
      setQuoteFile(savedQuoteFile);
      setRibFile(savedRibFile);
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('La connexion a expiré. Vos données ont été conservées. Veuillez réessayer.');
      } else if (err.code === 'ERR_NETWORK') {
        toast.error('Problème de connexion réseau. Vos données ont été conservées. Vérifiez votre connexion et réessayez.');
      } else {
        const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'envoi. Vos données ont été conservées. Veuillez réessayer.';
        toast.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignSettlement = async () => {
    if (!acceptedSettlement) {
      toast.error('Veuillez accepter les termes de la quittance');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/portal/third-party/${token}/sign-settlement`, {
        third_party_info: thirdPartyInfo
      });
      
      // Check if EDI Signature is being used
      if (response.data.use_edisignature || response.data.use_yousign) {
        // If there's a signing URL, redirect
        if (response.data.signing_url) {
          toast.success('Redirection vers la plateforme de signature électronique...');
          window.location.href = response.data.signing_url;
          return;
        }
        
        // EDI Signature will send an email - show waiting step
        await refreshData();
        setStep('waiting_signature_email');
        toast.success('Un email de signature électronique vous a été envoyé');
        return;
      }
      
      // Fallback: simulated signature completed
      await refreshData();
      setStep('waiting_payment');
      toast.success('Quittance signée avec succès');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSubmitting(false);
    }
  };

  // Poll for signature status when waiting for signature email
  useEffect(() => {
    if (step === 'waiting_signature_email') {
      const pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_URL}/portal/third-party/${token}/signature-status`);
          if (response.data.signed) {
            clearInterval(pollInterval);
            await refreshData();
            setStep('waiting_payment');
            toast.success('Signature confirmée ! Le dossier a été mis à jour automatiquement.');
          }
        } catch (err) {
          console.error('Error polling signature status:', err);
        }
      }, 10000); // Poll every 10 seconds
      
      return () => clearInterval(pollInterval);
    }
  }, [step, token]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="max-w-2xl mx-auto">
          <Skeleton className="h-12 w-48 mb-6" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Lien invalide</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="third-party-portal">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-4 md:px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Logo size="default" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 md:p-8">
        
        {/* C2 Assurances Trust Badge */}
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white mb-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Award className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-blue-900">{C2_INFO.name}</h3>
                <p className="text-sm text-blue-700">{C2_INFO.role}</p>
                <div className="mt-2 space-y-1 text-xs text-blue-600">
                  <p className="flex items-center gap-2">
                    <BadgeCheck className="w-4 h-4" />
                    Enregistré à l'ORIAS sous le n° <strong>{C2_INFO.orias}</strong>
                  </p>
                  <p className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {C2_INFO.address}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Progress indicator */}
        {!['welcome', 'refused'].includes(step) && (
          <Card className="border-slate-200 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between text-xs">
                {[
                  { key: 'upload_quote', label: 'Devis', icon: Upload },
                  { key: 'waiting_validation', label: 'Validation', icon: Clock },
                  { key: 'settlement', label: 'Signature', icon: FileText },
                  { key: 'waiting_payment', label: 'Paiement', icon: Euro },
                  { key: 'completed', label: 'Terminé', icon: CheckCircle2 }
                ].map((s, i) => {
                  const isActive = step === s.key;
                  const isPast = ['upload_quote', 'waiting_validation', 'settlement', 'waiting_payment', 'completed'].indexOf(step) > i;
                  const Icon = s.icon;
                  return (
                    <div key={s.key} className="flex flex-col items-center gap-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isActive ? 'bg-amber-500 text-white' : 
                        isPast ? 'bg-emerald-500 text-white' : 
                        'bg-slate-200 text-slate-400'
                      }`}>
                        {isPast ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                      </div>
                      <span className={isActive ? 'font-medium text-slate-900' : 'text-slate-500'}>{s.label}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Welcome Step */}
        {step === 'welcome' && (
          <div className="space-y-6" data-testid="step-welcome">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-emerald-900">Résolution amiable de votre sinistre</h2>
                    <p className="text-sm text-emerald-700 mt-1">
                      C2 Assurances intervient comme tiers de confiance pour faciliter la prise en charge 
                      des réparations de votre véhicule suite à ce sinistre matériel.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Dossier {data?.claim_reference}</CardTitle>
                <CardDescription>Sinistre du {formatDate(data?.incident_date)}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Car className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Véhicule impliqué</p>
                    <p className="font-medium">{data?.company_vehicle?.plate} - {data?.company_vehicle?.brand} {data?.company_vehicle?.model}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Description de l'incident</p>
                  <p className="font-medium">{data?.incident_description}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Comment ça marche ?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { icon: CheckCircle2, title: '1. Acceptez la procédure', desc: 'Vous acceptez la résolution amiable (facultatif)' },
                    { icon: Building2, title: '2. Choisissez votre garage', desc: 'Vous êtes libre de choisir le réparateur de votre choix' },
                    { icon: Upload, title: '3. Envoyez le devis + RIB', desc: 'Transmettez le devis du garage et son RIB' },
                    { icon: FileText, title: '4. Signez la quittance', desc: 'Signature électronique de la quittance transactionnelle' },
                    { icon: Euro, title: '5. Paiement direct', desc: 'L\'entreprise responsable paie directement le garage' },
                    { icon: Wrench, title: '6. Réparation', desc: 'Le garage effectue les réparations' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{item.title}</p>
                        <p className="text-sm text-slate-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Alert className="border-blue-200 bg-blue-50">
              <Shield className="h-4 w-4 text-blue-600" />
              <AlertTitle className="text-blue-900">Vos garanties</AlertTitle>
              <AlertDescription className="text-blue-700">
                <ul className="mt-2 space-y-1 text-sm">
                  <li>✓ <strong>Liberté totale</strong> : Vous pouvez accepter ou refuser</li>
                  <li>✓ <strong>Choix libre du garage</strong> : Aucune obligation</li>
                  <li>✓ <strong>Aucune avance de frais</strong> : Vous ne payez rien</li>
                  <li>✓ <strong>Paiement direct au garage</strong> : Sur présentation du RIB</li>
                  <li>✓ <strong>Dommages matériels uniquement</strong></li>
                </ul>
              </AlertDescription>
            </Alert>

            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="accept-terms"
                    checked={acceptedTerms}
                    onCheckedChange={setAcceptedTerms}
                    className="mt-1"
                    data-testid="accept-terms-checkbox"
                  />
                  <Label htmlFor="accept-terms" className="text-sm text-slate-600 cursor-pointer">
                    Je comprends que cette procédure amiable couvre <strong>uniquement les dommages matériels</strong> constatés 
                    sur mon véhicule. Je suis libre d'accepter ou de refuser cette démarche et de choisir le garage de mon choix.
                  </Label>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3 pt-4">
              <Button 
                onClick={handleAccept}
                disabled={submitting || !acceptedTerms}
                className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700"
                data-testid="accept-btn"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                J'accepte la procédure amiable
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => setStep('decision')}
                className="w-full h-14 text-lg"
                data-testid="refuse-choice-btn"
              >
                Je préfère passer par mon assurance
              </Button>
            </div>
          </div>
        )}

        {/* Decision Step */}
        {step === 'decision' && (
          <div className="space-y-6" data-testid="step-decision">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle>Vous préférez passer par votre assurance ?</CardTitle>
                <CardDescription>
                  C'est tout à fait votre droit. Vous pouvez indiquer la raison (optionnel).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Précisez votre raison si vous le souhaitez..."
                  value={refuseReason}
                  onChange={(e) => setRefuseReason(e.target.value)}
                  data-testid="refuse-reason-input"
                />
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setStep('welcome')} className="flex-1">
                    Retour
                  </Button>
                  <Button onClick={handleRefuse} disabled={submitting} className="flex-1 bg-slate-900 hover:bg-slate-800">
                    Confirmer mon choix
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Upload Quote Step */}
        {step === 'upload_quote' && (
          <div className="space-y-6" data-testid="step-upload-quote">
            <Alert className="border-emerald-200 bg-emerald-50">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertTitle className="text-emerald-900">Procédure amiable acceptée</AlertTitle>
              <AlertDescription className="text-emerald-700">
                Veuillez maintenant transmettre le devis de votre garage ainsi que son RIB pour le règlement.
              </AlertDescription>
            </Alert>

            {/* Garage Info */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  Informations du garage
                </CardTitle>
                <CardDescription>Le garage de votre choix pour les réparations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom du garage *</Label>
                  <Input
                    placeholder="Ex: Garage Dupont"
                    value={garage.name}
                    onChange={(e) => setGarage(prev => ({ ...prev, name: e.target.value }))}
                    className="h-12"
                    data-testid="garage-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input
                    placeholder="123 rue de la Réparation"
                    value={garage.address}
                    onChange={(e) => setGarage(prev => ({ ...prev, address: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Code postal</Label>
                    <Input
                      placeholder="75001"
                      value={garage.postal_code}
                      onChange={(e) => setGarage(prev => ({ ...prev, postal_code: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input
                      placeholder="Paris"
                      value={garage.city}
                      onChange={(e) => setGarage(prev => ({ ...prev, city: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Téléphone</Label>
                    <Input
                      placeholder="01 23 45 67 89"
                      value={garage.phone}
                      onChange={(e) => setGarage(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="garage@email.com"
                      value={garage.email}
                      onChange={(e) => setGarage(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quote Upload */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  Devis de réparation *
                </CardTitle>
                <CardDescription>Téléversez le devis établi par le garage</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Hidden file inputs */}
                <input
                  ref={quoteInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,image/*"
                  onChange={(e) => e.target.files[0] && setQuoteFile(e.target.files[0])}
                  className="hidden"
                  data-testid="quote-file-input"
                />
                <input
                  ref={quoteCameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => e.target.files[0] && setQuoteFile(e.target.files[0])}
                  className="hidden"
                  data-testid="quote-camera-input"
                />

                {quoteFile ? (
                  <div className="border-2 border-emerald-200 bg-emerald-50 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-emerald-500" />
                        <div>
                          <p className="font-medium text-slate-900">{quoteFile.name}</p>
                          <p className="text-sm text-slate-500">{(quoteFile.size / 1024).toFixed(1)} Ko</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setQuoteFile(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <p className="font-medium text-slate-700 mb-4">Téléverser le devis</p>
                    
                    {isMobile ? (
                      <div className="flex flex-col gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => quoteCameraRef.current?.click()}
                          className="w-full h-12"
                          data-testid="quote-camera-btn"
                        >
                          <Camera className="w-5 h-5 mr-2" />
                          Prendre une photo
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => quoteInputRef.current?.click()}
                          className="w-full h-12"
                          data-testid="quote-gallery-btn"
                        >
                          <FolderOpen className="w-5 h-5 mr-2" />
                          Choisir un fichier
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => quoteInputRef.current?.click()}
                        className="h-12"
                        data-testid="quote-upload-btn"
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        Sélectionner un fichier
                      </Button>
                    )}
                    <p className="text-sm text-slate-500 mt-3">PDF ou image (JPG, PNG)</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Montant du devis (€ TTC) *</Label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={quoteAmount}
                      onChange={(e) => setQuoteAmount(e.target.value)}
                      className="pl-10 h-12 text-lg"
                      data-testid="quote-amount-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* RIB Upload */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-slate-600" />
                  RIB du garage *
                </CardTitle>
                <CardDescription>Pour le règlement direct au garage</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Hidden file inputs */}
                <input
                  ref={ribInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,image/*"
                  onChange={(e) => e.target.files[0] && setRibFile(e.target.files[0])}
                  className="hidden"
                  data-testid="rib-file-input"
                />
                <input
                  ref={ribCameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => e.target.files[0] && setRibFile(e.target.files[0])}
                  className="hidden"
                  data-testid="rib-camera-input"
                />

                {ribFile ? (
                  <div className="border-2 border-emerald-200 bg-emerald-50 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-8 h-8 text-emerald-500" />
                        <div>
                          <p className="font-medium text-slate-900">{ribFile.name}</p>
                          <p className="text-sm text-slate-500">{(ribFile.size / 1024).toFixed(1)} Ko</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setRibFile(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <p className="font-medium text-slate-700 mb-4">Téléverser le RIB</p>
                    
                    {isMobile ? (
                      <div className="flex flex-col gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => ribCameraRef.current?.click()}
                          className="w-full h-12"
                          data-testid="rib-camera-btn"
                        >
                          <Camera className="w-5 h-5 mr-2" />
                          Prendre une photo
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => ribInputRef.current?.click()}
                          className="w-full h-12"
                          data-testid="rib-gallery-btn"
                        >
                          <FolderOpen className="w-5 h-5 mr-2" />
                          Choisir un fichier
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => ribInputRef.current?.click()}
                        className="h-12"
                        data-testid="rib-upload-btn"
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        Sélectionner un fichier
                      </Button>
                    )}
                    <p className="text-sm text-slate-500 mt-3">PDF ou image (JPG, PNG)</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Button 
              onClick={handleQuoteUpload}
              disabled={submitting || !garage.name || !quoteFile || !ribFile || !quoteAmount}
              className="w-full h-14 text-lg bg-amber-500 hover:bg-amber-600"
              data-testid="submit-quote-btn"
            >
              {submitting ? 'Envoi en cours...' : 'Envoyer les documents'}
            </Button>
          </div>
        )}

        {/* Waiting Validation Step */}
        {step === 'waiting_validation' && (
          <div className="space-y-6" data-testid="step-waiting-validation">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="py-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-blue-900 mb-2">En attente de validation</h2>
                <p className="text-blue-700">
                  Votre devis de <strong>{data?.quote_amount}€</strong> est en cours d'examen par notre équipe.
                  Vous serez notifié par email dès validation.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-3">Récapitulatif</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Garage</span>
                    <span className="font-medium">{data?.selected_garage?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Montant du devis</span>
                    <span className="font-medium">{data?.quote_amount}€ TTC</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settlement Step */}
        {step === 'settlement' && (
          <div className="space-y-6" data-testid="step-settlement">
            <Alert className="border-emerald-200 bg-emerald-50">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertTitle className="text-emerald-900">Devis validé</AlertTitle>
              <AlertDescription className="text-emerald-700">
                Veuillez maintenant signer la quittance transactionnelle pour finaliser l'accord.
              </AlertDescription>
            </Alert>

            <Card className="border-amber-200 bg-amber-50">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <img src="https://customer-assets.emergentagent.com/job_carcare-hub-20/artifacts/5capxlwx_LOGOTYPE_BLEU.png" alt="C2 Assurances" className="h-8" />
                </div>
                <CardTitle className="text-amber-900">Quittance transactionnelle</CardTitle>
                <CardDescription className="text-amber-700">Règlement amiable de sinistre</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-amber-200 text-sm">
                  <p className="font-semibold mb-3">Entre les soussignés :</p>
                  
                  <p className="mb-2">
                    La société <strong>{data?.organization_name || 'L\'entreprise responsable'}</strong>
                    {data?.organization_siret && <>, SIRET {data.organization_siret}</>}
                    {data?.organization_address && <>, dont le siège social est situé {data.organization_address}</>}, 
                    représentée par son mandataire C2 Assurances (ORIAS {C2_INFO.orias}), {C2_INFO.address}, 
                    ci-après dénommée <strong>"la Société"</strong>
                  </p>
                  
                  <p className="mb-4">Et :</p>
                  
                  <div className="space-y-3 mb-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Nom complet *</Label>
                      <Input
                        value={thirdPartyInfo.full_name}
                        onChange={(e) => setThirdPartyInfo(prev => ({ ...prev, full_name: e.target.value }))}
                        placeholder="M./Mme Nom Prénom"
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Adresse</Label>
                      <Input
                        value={thirdPartyInfo.address}
                        onChange={(e) => setThirdPartyInfo(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="Adresse complète"
                        className="h-9"
                      />
                    </div>
                  </div>
                  
                  <p className="mb-2">ci-après dénommé(e) <strong>"le Tiers"</strong></p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-amber-200 text-sm space-y-4">
                  <div>
                    <p className="font-semibold">1. Objet de la présente quittance</p>
                    <p className="mt-1 text-slate-600">
                      Le {formatDate(data?.incident_date)}, un accident de la circulation est survenu entre 
                      le véhicule immatriculé <strong>{data?.company_vehicle?.plate}</strong> appartenant à la Société 
                      et le véhicule du Tiers.
                    </p>
                    <p className="mt-2 text-slate-600">
                      À la suite de cet incident, les parties ont convenu d'un règlement amiable, sans recours 
                      à leurs assureurs respectifs, conformément aux termes du présent document.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">2. Évaluation du préjudice</p>
                    <p className="mt-1 text-slate-600">
                      Le Tiers a transmis les éléments nécessaires à l'évaluation du dommage (photographies 
                      et devis de réparation établi par un professionnel).
                    </p>
                    <p className="mt-2">
                      Sur la base de ces éléments, le préjudice a été évalué et accepté pour un montant total de :
                    </p>
                    <p className="text-2xl font-bold text-amber-900 mt-2">{data?.final_amount || data?.quote_amount}€ TTC</p>
                  </div>

                  <div>
                    <p className="font-semibold">3. Modalités de règlement</p>
                    <p className="mt-1 text-slate-600">
                      <strong>Règlement direct au réparateur</strong> : La somme de <strong>{data?.final_amount || data?.quote_amount}€ TTC</strong> sera 
                      réglée directement au garage <strong>{data?.selected_garage?.name}</strong> dont le RIB a été transmis 
                      par le Tiers parallèlement au devis.
                    </p>
                    <p className="mt-2 text-slate-600">
                      Le règlement interviendra après réception de la quittance signée par le Tiers.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">4. Effet libératoire</p>
                    <p className="mt-1 text-slate-600">
                      Le Tiers reconnaît recevoir le paiement de la somme précitée à titre transactionnel, 
                      intégral et définitif, en règlement du préjudice matériel résultant de l'accident 
                      du {formatDate(data?.incident_date)}.
                    </p>
                    <p className="mt-2 text-slate-600">
                      En conséquence, le Tiers déclare être intégralement indemnisé(e) et renonce à toute 
                      réclamation ou recours ultérieur contre la Société, son conducteur, son assureur, 
                      ainsi que son mandataire C2 Assurances.
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">5. Subrogation et conservation</p>
                    <p className="mt-1 text-slate-600">
                      La présente quittance vaut transaction au sens de l'article 2044 du Code civil et 
                      produit les effets de l'article 2052 du Code civil, ayant autorité de chose jugée 
                      entre les parties.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="accept-settlement"
                    checked={acceptedSettlement}
                    onCheckedChange={setAcceptedSettlement}
                    className="mt-1"
                    data-testid="accept-settlement-checkbox"
                  />
                  <Label htmlFor="accept-settlement" className="text-sm cursor-pointer">
                    <strong>Lu et approuvé, bon pour accord transactionnel.</strong> Je reconnais avoir pris 
                    connaissance des termes de la présente quittance et les accepter sans réserve.
                  </Label>
                </div>

                <Button 
                  onClick={handleSignSettlement}
                  disabled={submitting || !acceptedSettlement || !thirdPartyInfo.full_name}
                  className="w-full h-14 text-lg bg-amber-500 hover:bg-amber-600"
                  data-testid="sign-settlement-btn"
                >
                  {submitting ? (
                    <>
                      <Clock className="w-5 h-5 mr-2 animate-spin" />
                      Préparation de la signature...
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 mr-2" />
                      Déclencher la signature électronique
                    </>
                  )}
                </Button>

                <Alert className="border-blue-200 bg-blue-50">
                  <AlertTriangle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-sm">
                    <strong>Prochaine étape :</strong> En cliquant sur ce bouton, vous allez recevoir un email de notre partenaire 
                    <strong> EDI Signature (Docapost)</strong> vous invitant à signer électroniquement la quittance.
                    Vérifiez votre boîte de réception (et vos spams).
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            <Alert className="border-slate-200">
              <Lock className="h-4 w-4" />
              <AlertDescription className="text-slate-600 text-xs">
                {C2_INFO.name} - {C2_INFO.role}<br/>
                ORIAS n°{C2_INFO.orias} • {C2_INFO.address}
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Waiting for Signature Email Step */}
        {step === 'waiting_signature_email' && (
          <div className="space-y-6" data-testid="step-waiting-signature-email">
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-amber-900 mb-2">Vérifiez vos emails</h2>
                <p className="text-amber-700 mb-4">
                  Un email de <strong>EDI Signature (Docapost)</strong> vous a été envoyé pour signer électroniquement la quittance.
                </p>
                <div className="bg-white rounded-lg p-4 border border-amber-200 text-left text-sm">
                  <p className="font-semibold mb-2">📧 Prochaines étapes :</p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Vérifiez votre boîte de réception (et vos spams)</li>
                    <li>Ouvrez l'email d'EDI Signature</li>
                    <li>Cliquez sur le lien pour signer la quittance</li>
                    <li>Revenez ici et cliquez sur "J'ai signé"</li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            <Alert className="border-emerald-200 bg-emerald-50">
              <Clock className="h-4 w-4 text-emerald-600 animate-spin" />
              <AlertDescription className="text-emerald-800 text-sm">
                <strong>Vérification automatique en cours...</strong> Cette page se met à jour automatiquement dès que votre signature est détectée.
              </AlertDescription>
            </Alert>

            <Button 
              onClick={async () => {
                setSubmitting(true);
                try {
                  const response = await axios.get(`${API_URL}/portal/third-party/${token}/signature-status`);
                  if (response.data.signed) {
                    await refreshData();
                    setStep('waiting_payment');
                    toast.success('Signature confirmée !');
                  } else {
                    toast.info('Signature pas encore détectée. Veuillez d\'abord signer via l\'email EDI Signature.');
                  }
                } catch (err) {
                  toast.error('Erreur lors de la vérification');
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
              variant="outline"
              className="w-full h-14 text-lg"
              data-testid="check-signature-btn"
            >
              {submitting ? (
                <>
                  <Clock className="w-5 h-5 mr-2 animate-spin" />
                  Vérification...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  Vérifier manuellement
                </>
              )}
            </Button>

            <Alert className="border-blue-200 bg-blue-50">
              <AlertTriangle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800 text-sm">
                <strong>Email envoyé à :</strong> {data?.third_party?.email || thirdPartyInfo.email}
                <br/>
                Si vous ne recevez pas l'email dans les prochaines minutes, vérifiez vos spams ou contactez-nous.
              </AlertDescription>
            </Alert>

            <Alert className="border-slate-200">
              <Lock className="h-4 w-4" />
              <AlertDescription className="text-slate-600 text-xs">
                {C2_INFO.name} - {C2_INFO.role}<br/>
                ORIAS n°{C2_INFO.orias} • {C2_INFO.address}
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Waiting Payment Step */}
        {step === 'waiting_payment' && (
          <div className="space-y-6" data-testid="step-waiting-payment">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="py-8 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold text-emerald-900 mb-2">Quittance signée !</h2>
                <p className="text-emerald-700">
                  Le règlement de <strong>{data?.final_amount || data?.quote_amount}€</strong> va être effectué 
                  directement au garage <strong>{data?.selected_garage?.name}</strong>.
                </p>
              </CardContent>
            </Card>

            {/* EDI Signature Info */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Votre quittance signée
                </h3>
                <p className="text-sm text-blue-700 mb-2">
                  La quittance transactionnelle signée avec le cachet de signature électronique vous a été envoyée par email par <strong>EDI Signature (Docapost)</strong>.
                </p>
                <p className="text-sm text-blue-600">
                  Consultez votre boîte de réception pour télécharger votre copie.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-3">Prochaines étapes</h3>
                <ol className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">✓</span>
                    <span className="text-slate-400">Quittance signée</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">→</span>
                    <span className="font-medium text-blue-700">Virement en cours vers le garage</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">3</span>
                    <span>Réparations par le garage</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">4</span>
                    <span>Clôture du dossier</span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Alert>
              <AlertDescription>
                Vous pouvez prendre rendez-vous avec votre garage pour les réparations. 
                Le paiement sera effectué dès confirmation.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Completed Step */}
        {step === 'completed' && (
          <div className="space-y-6" data-testid="step-completed">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="py-8 text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-emerald-900 mb-2">Dossier clôturé</h2>
                <p className="text-emerald-700">
                  Le règlement amiable de votre sinistre est terminé. Merci de votre confiance.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-3">Récapitulatif</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Dossier</span>
                    <span className="font-medium">{data?.claim_reference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Montant réglé</span>
                    <span className="font-medium">{data?.final_amount || data?.quote_amount}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Garage</span>
                    <span className="font-medium">{data?.selected_garage?.name}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Refused Step */}
        {step === 'refused' && (
          <div className="text-center py-8" data-testid="step-refused">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-slate-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Choix enregistré</h2>
            <p className="text-slate-600 mb-6">
              Nous avons pris note de votre choix de passer par votre assurance.
            </p>
            <Alert className="text-left">
              <AlertDescription>
                Vous pouvez contacter votre assurance pour déclarer ce sinistre. 
                Si vous changez d'avis, n'hésitez pas à nous recontacter.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </main>

      <footer className="py-6 text-center text-sm text-slate-500 border-t border-slate-200 mt-8">
        <p>© 2025 {C2_INFO.name}</p>
        <p className="mt-1 text-xs">ORIAS n°{C2_INFO.orias} • {C2_INFO.address}</p>
        <p className="mt-1 text-xs">Données traitées conformément au RGPD</p>
      </footer>
    </div>
  );
};

export default ThirdPartyPortal;
